import { Search as SearchIcon } from "@mui/icons-material";
import Delete from "@mui/icons-material/Delete";
import PlayArrow from "@mui/icons-material/PlayArrow";
import { FormControl, Grid, IconButton } from "@mui/material";
import InputLabel from "@mui/material/InputLabel";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Pagination from "@mui/material/Pagination";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import useMediaQuery from "@mui/material/useMediaQuery";
import * as React from "react";
import { getAllExpositors } from "../../../services/expositor.service";
import { deleteGallery, getAllPics } from "../../../services/gallery.service";
import { ModalComponent } from "../../../ui-components/Modal";
import SectionTitle from "../../../ui-components/SectionTitle";
import { useConfirm } from "../../../ui-components/ConfirmProvider";
import { useToast } from "../../../ui-components/ToastProvider";

export default function Gallery() {
  type UserOption = { userId: number; userName: string; items: any[] };

  const [gallery, setGallery] = React.useState([]);
  const [expositors, setExpositors] = React.useState([]);
  const [page, setPage] = React.useState(1);
  const [itemsPerPage, setItemsPerPage] = React.useState(12);
  const [tabIndex, setTabIndex] = React.useState(0);
  const [previewItem, setPreviewItem] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [query, setQuery] = React.useState("");

  React.useEffect(() => {
    getAllPics().then((x) => {
      setGallery(x);
      setPage(1);
      setLoading(false);
    });
  }, []);

  React.useEffect(() => {
    getAllExpositors()
      .then((x) => x.json())
      .then((x) => setExpositors(x));
  }, []);

  // Mapa de expositores por id para acesso rápido
  const expositorsById = React.useMemo(() => {
    const map = new Map();
    (expositors || []).forEach((x) => map.set(x.id, x));
    return map;
  }, [expositors]);

  // Agrupar itens da galeria por usuário
  const usersWithItems = React.useMemo(() => {
    const grouped = new Map();
    (gallery || []).forEach((item) => {
      const arr = grouped.get(item.user_id) || [];
      arr.push(item);
      grouped.set(item.user_id, arr);
    });

    const list = Array.from(grouped.entries()).map(([userId, items]) => ({
      userId,
      userName: expositorsById.get(userId)?.name || "Usuário desconhecido",
      items,
    }));

    // Ordena por nome de usuário para uma experiência consistente
    list.sort((a, b) => a.userName.localeCompare(b.userName));
    return list;
  }, [gallery, expositorsById]);

  // Ajusta tabIndex se a lista de usuários mudar
  React.useEffect(() => {
    if (usersWithItems.length === 0) {
      setTabIndex(0);
      setPage(1);
      return;
    }
    if (tabIndex >= usersWithItems.length) {
      setTabIndex(0);
    }
    // reset de página ao mudar dados
    setPage(1);
  }, [usersWithItems, tabIndex]);

  const currentUser = usersWithItems[tabIndex];
  // filtro simples por nome do arquivo
  const currentItems = (currentUser?.items || []).filter((it) =>
    (it?.name || "").toLowerCase().includes(query.toLowerCase())
  );
  const totalPages = Math.max(
    1,
    Math.ceil((currentItems.length || 0) / itemsPerPage)
  );
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const pageItems = currentItems.slice(startIndex, endIndex);

  const isVideo = (url) => /\.(mp4|webm|ogg)$/i.test(url || "");
  const theme = useTheme();
  const lgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));
  const smUp = useMediaQuery(theme.breakpoints.up("sm"));
  const cols = lgUp ? 4 : mdUp ? 3 : smUp ? 2 : 1;
  const { confirm } = useConfirm();
  const { showToast } = useToast();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 2,
        width: "100%",
        mb: 2,
      }}
    >
      <SectionTitle title="Galeria" />

      {/* Toolbar de filtro responsiva: usuário, busca por arquivo e tamanho de página */}
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={5}>
          <Autocomplete<UserOption>
            size="small"
            sx={{ width: "100%" }}
            options={usersWithItems as UserOption[]}
            value={(usersWithItems[tabIndex] as UserOption) || null}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.userName || ""
            }
            isOptionEqualToValue={(option, value) =>
              typeof value === "string"
                ? option.userName === value
                : option.userId === value.userId
            }
            onChange={(event, newValue) => {
              if (newValue && typeof newValue !== "string") {
                const idx = usersWithItems.findIndex(
                  (u: any) => u.userId === newValue.userId
                );
                setTabIndex(idx >= 0 ? idx : 0);
                setPage(1);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Pesquisar usuário"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={5}>
          <TextField
            size="small"
            fullWidth
            placeholder="Pesquisar por nome do arquivo"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="items-per-page-label">Itens por página</InputLabel>
            <Select
              labelId="items-per-page-label"
              label="Itens por página"
              value={String(itemsPerPage)}
              onChange={(e: SelectChangeEvent) => {
                setItemsPerPage(Number(e.target.value));
                setPage(1);
              }}
            >
              <MenuItem value={"8"}>8</MenuItem>
              <MenuItem value={"12"}>12</MenuItem>
              <MenuItem value={"16"}>16</MenuItem>
              <MenuItem value={"24"}>24</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Tabs de usuários */}
      <Tabs
        value={tabIndex}
        onChange={(e, value) => {
          setTabIndex(value);
          setPage(1);
        }}
        textColor="primary"
        indicatorColor="primary"
        variant="scrollable"
        scrollButtons
        allowScrollButtonsMobile
        sx={{ mb: 2 }}
      >
        {usersWithItems.map((u) => (
          <Tab
            key={u.userId}
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {u.userName}
                <Chip
                  label={u.items.length}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
            }
          />
        ))}
      </Tabs>

      <ImageList variant="masonry" cols={cols} gap={12}>
        {loading &&
          Array.from({ length: cols * 3 }).map((_, i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              height={180}
              sx={{ borderRadius: 2 }}
            />
          ))}
        {!loading &&
          pageItems &&
          pageItems.map((item) => (
            <ImageListItem key={item.id}>
              <Box
                sx={{
                  position: "relative",
                  borderRadius: 2,
                  overflow: "hidden",
                  boxShadow: 1,
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": { transform: "scale(1.01)", boxShadow: 3 },
                }}
              >
                <Box
                  sx={{
                    aspectRatio: isVideo(item.url) ? "9/16" : "16/9",
                    width: "100%",
                    backgroundColor: "action.hover",
                  }}
                >
                  {isVideo(item.url) ? (
                    <Box
                      onClick={() => setPreviewItem(item)}
                      sx={{ cursor: "pointer", height: "100%" }}
                    >
                      <video
                        src={item.url}
                        preload="metadata"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Box
                          sx={{
                            bgcolor: "rgba(0,0,0,0.35)",
                            width: 64,
                            height: 64,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <PlayArrow sx={{ color: "#fff" }} />
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      onClick={() => setPreviewItem(item)}
                      sx={{ cursor: "zoom-in", height: "100%", minHeight: 320 }}
                    >
                      <img
                        src={item.url}
                        alt={item.name}
                        loading="lazy"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </Box>
                  )}
                </Box>

                {/* Overlay elegante */}
                <Box
                  className="overlay"
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    bgcolor: "rgba(0,0,0,0.45)",
                    color: "#fff",
                    backdropFilter: "blur(2px)",
                  }}
                >
                  <Box
                    sx={{
                      fontSize: 14,
                      fontWeight: 500,
                      flex: 1,
                      minWidth: 0,
                      pr: 1,
                      whiteSpace: "normal",
                      overflowWrap: "break-word",
                      wordBreak: "break-word",
                      lineHeight: 1.4,
                    }}
                  >
                    {isVideo(item.url) ? "Vídeo" : "Imagem"}
                    {item.name ? ` • ${item.name}` : ""}
                  </Box>
                  <IconButton
                    size="small"
                    sx={{ color: "#fff" }}
                    aria-label={`delete ${item.name || "arquivo"}`}
                    onClick={async (e) => {
                      const ok = await confirm({
                        title: "Apagar arquivo",
                        description:
                          "Deseja apagar este arquivo? Essa operação pode ser irreversível",
                        confirmText: "Apagar",
                        cancelText: "Cancelar",
                      });
                      if (!ok) return;
                      deleteGallery(item.id).then(() => {
                        getAllPics().then((x) => {
                          setGallery(x);
                          showToast("Arquivo apagado com sucesso!", { severity: "success" });
                        });
                      });
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </ImageListItem>
          ))}
      </ImageList>
      <Grid container justifyContent="center" mt={2}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(e, value) => setPage(value)}
          color="primary"
          showFirstButton
          showLastButton
        />
      </Grid>

      {/* Preview Modal - estilo consistente */}
      <ModalComponent
        open={Boolean(previewItem)}
        handleClose={() => setPreviewItem(null)}
        title={
          isVideo(previewItem?.url || "")
            ? "Pré-visualização do Vídeo"
            : "Pré-visualização da Imagem"
        }
        autoHeight
        maxWidth={900}
      >
        {previewItem && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isVideo(previewItem.url) ? (
              <video
                src={previewItem.url}
                controls
                autoPlay
                style={{
                  width: "100%",
                  height: "auto",
                  maxHeight: "75vh",
                  objectFit: "contain",
                  borderRadius: 12,
                }}
              />
            ) : (
              <img
                src={previewItem.url}
                alt={previewItem.name}
                style={{
                  width: "100%",
                  height: "auto",
                  maxHeight: "75vh",
                  objectFit: "contain",
                  borderRadius: 12,
                }}
              />
            )}
          </Box>
        )}
      </ModalComponent>
    </Box>
  );
}
